import { useEffect, useState } from "react";
import { BadgePercent, X, MessageCircle, ArrowRight } from "lucide-react";
import { fetchPublic } from "../lib/content";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { WA_LINK } from "../lib/constants";

export const FloatingOffer = () => {
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchPublic("offers").then(setOffers).catch(() => setOffers([]));
  }, []);

  if (!offers.length) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="floating-offer-button"
        aria-label="View running offers"
        className="fixed bottom-24 right-5 z-40 group"
      >
        <span className="absolute inset-0 rounded-full bg-amber-400/30 animate-ping group-hover:hidden" />
        <span className="relative flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-2xl shadow-amber-500/40 transition-all hover:scale-105">
          <BadgePercent className="w-5 h-5" />
          <span className="text-sm">Offers</span>
          <span className="bg-white text-amber-600 text-[10px] font-black rounded-full px-1.5 py-0.5">
            {offers.length}
          </span>
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="offers-dialog">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="font-heading text-2xl font-extrabold text-[#0A192F]">
              <BadgePercent className="w-6 h-6 inline mr-2 text-amber-500" />
              Running Offers
            </DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">
              Limited-time deals — message us on WhatsApp to claim.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-6 space-y-4">
            {offers.map((o, i) => (
              <article key={o.id} data-testid={`offer-${i}`} className="rounded-2xl overflow-hidden border border-slate-200">
                {o.image ? (
                  <div className="aspect-[16/7] overflow-hidden bg-slate-100">
                    <img src={o.image} alt={o.title} className="w-full h-full object-cover" />
                  </div>
                ) : null}
                <div className="p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-heading text-lg font-bold text-[#0A192F]">{o.title}</h3>
                    {o.valid_until ? (
                      <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] uppercase tracking-wider font-bold">
                        Till {o.valid_until}
                      </span>
                    ) : null}
                  </div>
                  {o.description ? <p className="mt-2 text-sm text-[#475569] leading-relaxed">{o.description}</p> : null}
                  <a
                    href={o.link || WA_LINK(`Hi TASSEI, I want to claim the "${o.title}" offer`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`offer-cta-${i}`}
                  >
                    <Button className="mt-4 bg-[#00B37E] hover:bg-[#009166] text-white rounded-full h-10 px-5 font-semibold">
                      <MessageCircle className="w-4 h-4 mr-2" /> Claim on WhatsApp
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </a>
                </div>
              </article>
            ))}
            <Button variant="outline" onClick={() => setOpen(false)} className="w-full rounded-full">
              <X className="w-4 h-4 mr-2" /> Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FloatingOffer;
